import React from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'immutable';
import { selectDeckPile, selectGameTurn, selectIsActivePlayer } from '../ActiveGame/selectors';
import { Backdrop, Body, Header, Sheet, Title } from '../ContextMenu/styled';
import SelectPile from '../SelectPile';
import { Button, Muted } from '../Controls';
import { look } from './actions';
import { POKEDEX, TRADE_POKEMON } from './constants';
import { selectSendingLook } from './selectors';

/**
 * Computer Search, Pokédex or Pokémon Trader, once played: the player looks at their deck, which only they are sent, and picks. There
 * is no going back; the card is already played. Computer Search takes 1 card; Pokédex puts the cards back in the
 * order tapped, top card first.
 */
const DeckLook = ({ userId }) => {
  const dispatch = useDispatch();
  const gameTurn = useSelector(selectGameTurn);
  const isMyTurn = useSelector((state) => selectIsActivePlayer(state, { userId }));
  const deckPile = useSelector((state) => selectDeckPile(state, { userId }));
  const sending = useSelector(selectSendingLook);
  const looking = gameTurn && gameTurn.get('looking');

  if (!isMyTurn || !looking || !deckPile) {
    return null;
  }

  const cards = deckPile.get('cards') || List();
  const pokedex = looking.get('kind') === POKEDEX;
  const label = looking.get('label');
  const count = looking.get('count');
  const trade = looking.get('kind') === TRADE_POKEMON;
  const messages = {
    [POKEDEX]: `Tap the ${count} cards in the order you want them, top card first.`,
    [TRADE_POKEMON]: 'Choose 1 Basic Pokémon or Evolution card from your deck to put into your hand. Your deck is shuffled afterward, and your opponent sees both cards.',
  };
  const message = messages[looking.get('kind')] || 'Choose 1 card from your deck to put into your hand. Your deck is shuffled afterward.';

  return createPortal(
    <>
      <Backdrop />
      <Sheet role="dialog" aria-label={label}>
        <Header><Title>{label}</Title></Header>
        <Body>
          {trade && cards.size === 0 ? (
            <>
              <Muted>There are no Pokémon cards in your deck, so you take nothing. Your deck is shuffled.</Muted>
              <Button $primary $block disabled={sending} onClick={() => dispatch(look(List()))}>Done</Button>
            </>
          ) : <SelectPile
            key={looking.get('kind')}
            cardsOrCardables={cards}
            message={message}
            selectCount={pokedex ? count : 1}
            selectedIds={List()}
            numbered={pokedex}
            newestFirst
            doneLabel={pokedex ? 'Put them back' : 'Take it'}
            onSelectDone={ids => !sending && dispatch(look(
              ids.map(id => cards.find(card => card.get('pivot_id') === id).get('order')),
            ))}
          />}
        </Body>
      </Sheet>
    </>,
    document.body,
  );
};

DeckLook.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default DeckLook;
